import macroPoint from '@assets/macroPoint.svg';
import project44 from '@assets/project44.svg';
import truckerTools from '@assets/TruckerTools.png';
import { TimePicker } from '@components/TimePicker';
import {
  Checkbox,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@atgfr8/atg-ui-tailwind';
import {
  AlertIcon,
  ArrowDownCircleIcon,
  ArrowUpCircleIcon,
  CalendarMonthIcon,
  CloseCircleIcon,
  InformationIcon,
} from '@atgfr8/icons';
import {
  getTrackingProviderLabel,
  isValidEmailChain,
} from '@common/order/helperFunctions';
import {
  BasicTable,
  Button,
  CautionInput,
  CautionPhoneInput,
  Input,
  SelectInput,
  ToastType,
} from '@components';
import { Column } from '@components/BasicTable/BasicTable';
import { FeatureFlags } from '@constants/featureFlags';
import { createForm } from '@felte/solid';
import { validator } from '@felte/validator-yup';
import { isFeatureFlagEnabled } from '@store/featureFlags/store';
import { closeModal } from '@store/modals';
import {
  coveredByEmail,
  eldAssetList,
  enableLoadTracking,
  featureFlagGetCustomerFeatures,
  index,
  isReefer,
  load,
  loadTrackingRequest,
  orderRepEmail,
  orderStore,
  setOrderStore,
  TrackingData,
  updateLoadPropertyAtIndex,
  validateFieldForOrder,
} from '@store/orders';
import { trackingFrequency } from '@store/orders/constant';
import { TrackingProviders } from '@store/orders/types';
import {
  ILoadEditViewModel,
  TrackingStopWindow,
} from '@typeDefinitions/loadTypes';
import { LastUsedTopStopModel } from '@typeDefinitions/stopTypes';
import { formatDataTestId } from '@utils/dataTestIdFormatter';
import {
  formatDateString as formatDate,
  timeZoneAbbreviation,
} from '@utils/dateFormat';
import { handleToast, printLog } from '@utils/utils';
import { createSignal, onCleanup, onMount, Show } from 'solid-js';
import * as yup from 'yup';

import cls from './classes';
import { EldCaution } from './EldCaution';
import { TrackingBlocked } from './TrackingBlocked';
import { TruckerToolsCaution } from './TruckerToolsCaution';

type Props = {
  load: ILoadEditViewModel;
  modalId: string;
  tabIndex: number;
  setTracking: (e: boolean) => void;
};

// eslint-disable-next-line complexity
export const AcceptTrackingModal = (props: Props) => {
  const [isMacroLoading, setMacroloading] = createSignal(false);
  const [isProject44Loading, setProject44Loading] = createSignal(false);
  const [isTruckerToolsLoading, setTruckerToolsLoading] = createSignal(false);
  const [isProjectOverride, setProjectOverride] = createSignal(false);
  const [isMacroOverride, setMacroOverride] = createSignal(false);
  const [hasTrackingPreference, setTrackingPreference] = createSignal(false);
  const [eldAssetMatch, setEldAssetMatch] = createSignal(false);
  const [isTruckerToolsCustomer, setIsTruckerToolsCustomer] =
    createSignal(false);
  const [isTruckerToolsOffice, setIsTruckerToolsOffice] = createSignal(false);
  const [isTruckNumberDirty, setIsTruckNumberDirty] = createSignal(false);
  const [isTrailerNumberDirty, setIsTrailerNumberDirty] = createSignal(false);
  const [isPhoneNumberDirty, setIsPhoneNumberDirty] = createSignal(false);
  const [hasPhoneNumber, setHasPhoneNumber] = createSignal(false);
  const [shipperEmailError, setShipperEmailError] = createSignal<string | null>(
    null,
  );
  const [brokerEmailError, setBrokerEmailError] = createSignal<string | null>(
    null,
  );
  const [ttSubmitLabel, setTtSubmitLabel] =
    createSignal<string>('INITIATE  TRACKING');
  const [carrierDispatchEmailError, setCarrierDispatchEmailError] =
    createSignal<string | null>(null);

  const noTracking = () => {
    return !eldAssetMatch() && !hasPhoneNumber();
  };
  const validations = yup.object({
    trackingEmailMacro: yup
      .string()
      .nullable()
      .max(200, 'Max 200 characters')
      .test('email-chain', 'Invalid email(s)', (value) =>
        isValidEmailChain(value ?? ''),
      ),
    trackingEmailTruckerTools: yup.string().email('Invalid email').nullable(),
  });

  const initialValues = {
    serviceId: null,
    settings: {
      truckNumber: '',
      trailerNumber: '',
      trackingEmail: '',
      TrackingFrequency: '',
      timeZone: timeZoneAbbreviation(),
    },
    driverPhone: '',
    loadId: 0,
    trackingStartDate: new Date().toISOString(),
    truckNumberMacro: '',
    trailerNumberMacro: '',
    trackingEmailMacro: '',
    TrackingFrequencyMacro: '',
    truckNumberTruckerTools: '',
    trailerNumberTruckerTools: '',
    trackingEmailTruckerTools: '',
    shipperEmailTruckerTools: '',
    brokerEmailTruckerTools: '',
    carrierDispatchEmailTruckerTools: '',
    eldAssetId: '',
    minTempTruckerTools: null,
    maxTempTruckerTools: null,
    tempUomTruckerTools: '',
    timezone: timeZoneAbbreviation(),
    trackingStopWindows: [] as TrackingStopWindow[],
  };

  const iconStyles = { color: '#0070A2' };

  const DateCell = (props: { rowData: LastUsedTopStopModel }) => (
    <div class="flex items-center mb-[40px]">
      <span class="mr-4">
        {props.rowData.pickUp && <ArrowUpCircleIcon style={iconStyles} />}
        {props.rowData.dropOff && <ArrowDownCircleIcon style={iconStyles} />}
      </span>
      <div class="flex gap-2">
        <Typography component="p" variant="inherit">
          {formatDate(props.rowData.stopDateTime, 'MMM dd yyyy')}
        </Typography>
        <CalendarMonthIcon style={iconStyles} />
      </div>
    </div>
  );

  const TimeCell = (props: { rowData: LastUsedTopStopModel }) => (
    <div class="mb-[40px]">{props.rowData.time}</div>
  );

  const LocationCell = (props: { rowData: LastUsedTopStopModel }) => (
    <Stack spacing={1}>
      <Typography
        component="p"
        sxProps={{ fontWeight: 'bold' }}
        variant="inherit"
      >
        {props.rowData.locationName}
      </Typography>
      <Typography component="p" variant="inherit">
        {props.rowData.address1}, {props.rowData.address2}, {props.rowData.city}
        , {props.rowData.state}
      </Typography>
      <Typography component="p" variant="inherit">
        {props.rowData.contact}
      </Typography>
    </Stack>
  );

  const ArrivalTimeCell = (props: { rowData: LastUsedTopStopModel }) => (
    <div class="mb-[40px]">
      <TimePicker
        value={
          (data().trackingStopWindows.length &&
            data().trackingStopWindows[props.rowData.stopOrder ?? 0].ETA) ||
          ''
        }
        onChange={(newTime) => {
          if (props.rowData.stopDateTime != null) {
            const stopDateOnly = props.rowData.stopDateTime.split('T')[0];
            const etaTimeOnly = newTime.split('T')[1];
            const newStopDate = `${stopDateOnly}T${etaTimeOnly}`;
            setFields(
              // @ts-expect-error Setter.
              `trackingStopWindows[${props.rowData.stopOrder}].ETA`,
              newStopDate,
            );
          } else {
            setFields(
              // @ts-expect-error Setter.
              `trackingStopWindows[${props.rowData.stopOrder}].ETA`,
              newTime,
            );
          }
        }}
        dataTestId="eta-timePicker"
      />
    </div>
  );

  const DepartureTimeCell = (props: { rowData: LastUsedTopStopModel }) => (
    <div class="mb-[40px]">
      <TimePicker
        value={
          (data().trackingStopWindows.length &&
            data().trackingStopWindows[props.rowData.stopOrder ?? 0].EDT) ||
          ''
        }
        onChange={(newTime) => {
          if (props.rowData.stopDateTime != null) {
            const stopDateOnly = props.rowData.stopDateTime.split('T')[0];
            const etaTimeOnly = newTime.split('T')[1];
            const newStopDate = `${stopDateOnly}T${etaTimeOnly}`;
            setFields(
              // @ts-expect-error Setter.
              `trackingStopWindows[${props.rowData.stopOrder}].EDT`,
              newStopDate,
            );
          } else {
            setFields(
              // @ts-expect-error Setter.
              `trackingStopWindows[${props.rowData.stopOrder}].EDT`,
              newTime,
            );
          }
        }}
        dataTestId="edt-timePicker"
      />
    </div>
  );

  const columns: Column<LastUsedTopStopModel>[] = [
    {
      name: 'Date',
      key: 'date',
      minWidth: 200,
      renderCell: (rowData) => <DateCell rowData={rowData} />,
    },
    {
      name: 'Time',
      key: 'time',
      minWidth: 120,
      renderCell: (rowData) => <TimeCell rowData={rowData} />,
    },
    {
      name: 'Location',
      key: 'location',
      maxWidth: 220,
      renderCell: (rowData) => <LocationCell rowData={rowData} />,
    },
    {
      name: 'Est. Arrival Time',
      key: 'ArrivalTime',
      renderCell: (rowData) => <ArrivalTimeCell rowData={rowData} />,
    },
    {
      name: 'Est. Departure Time',
      key: 'DepartureTime',
      renderCell: (rowData) => <DepartureTimeCell rowData={rowData} />,
    },
  ];

  const startLoader = (serviceId: number) => {
    if (serviceId === TrackingProviders.Macropoint) {
      setMacroloading(true);
    } else if (serviceId === TrackingProviders.Project44) {
      setProject44Loading(true);
    } else if (serviceId === TrackingProviders.TruckerTools) {
      setTruckerToolsLoading(true);
    }
  };

  const stopLoader = (serviceId: number) => {
    if (serviceId === TrackingProviders.Macropoint) {
      setMacroloading(false);
    } else if (serviceId === TrackingProviders.Project44) {
      setProject44Loading(false);
    } else if (serviceId === TrackingProviders.TruckerTools) {
      setTruckerToolsLoading(false);
    }
  };

  const { data, form, setFields, errors } = createForm<TrackingData>({
    initialValues: initialValues,
    onSubmit: async (values) => {
      try {
        if (values.serviceId === null) {
          handleToast(ToastType.Error, 'Please select a tracking service');
          return;
        }
        if (values.serviceId === TrackingProviders.TruckerTools) {
          values.settings.truckNumber = values.truckNumberTruckerTools;
          values.settings.trailerNumber = values.trailerNumberTruckerTools;
          values.settings.trackingEmail = values.trackingEmailTruckerTools;
          values.settings.shipperEmail = values.shipperEmailTruckerTools;
          values.settings.brokerEmail = values.brokerEmailTruckerTools;
          values.settings.carrierDispatchEmail =
            values.carrierDispatchEmailTruckerTools;
          values.settings.minTemp =
            values.minTempTruckerTools === null
              ? ''
              : values.minTempTruckerTools.toString();
          values.settings.maxTemp =
            values.maxTempTruckerTools === null
              ? ''
              : values.maxTempTruckerTools.toString();
          values.settings.tempUom = values.tempUomTruckerTools;
          values.settings.eldAssetId = values.eldAssetId;
        } else if (values.serviceId === TrackingProviders.Macropoint) {
          values.settings.truckNumber = values.truckNumberMacro;
          values.settings.trailerNumber = values.trailerNumberMacro;
          values.settings.trackingEmail = values.trackingEmailMacro;
          values.settings.TrackingFrequency = values.TrackingFrequencyMacro;
        } else if (values.serviceId === TrackingProviders.Project44) {
          values.settings.truckNumber = props.load.truckNumber ?? '';
          values.settings.trailerNumber = props.load.trailerNumber ?? '';
          values.settings.trackingEmail = props.load.trackingEmail ?? '';
        }
        const postTrackingPayload = {
          loadId: values.loadId,
          trackingStartDate: values.trackingStartDate,
          serviceId: values.serviceId,
          trackingStopWindows: values.trackingStopWindows,
          driverPhone: values.driverPhone,
          settings: values.settings,
        };
        startLoader(values.serviceId);
        const response = await enableLoadTracking(postTrackingPayload);
        stopLoader(values.serviceId);

        const updateTruckNumber =
          values.serviceId === TrackingProviders.TruckerTools &&
          !eldAssetMatch() &&
          values.eldAssetId !== '' &&
          props.load.truckNumber !== values.eldAssetId;

        // @ts-expect-error Setter.
        setOrderStore('order', 'loads', props.tabIndex, (load) => {
          return {
            ...load,
            trackingServiceId: values.serviceId,
            truckNumber: updateTruckNumber
              ? values.eldAssetId
              : props.load.truckNumber,
            operationType: 'Update',
          };
        });

        // The `enableTracking` function ensures that if the call did not succeed, then
        // it will throw an error. So we can just handle the success here.
        if (response) {
          handleToast(ToastType.Success, response.message);
          closeModal(props.modalId);
        }
      } catch (error) {
        if (values.serviceId !== null) {
          stopLoader(values.serviceId);
        }
        handleToast(ToastType.Error, (error as Error).message);
      }
    },
    onError: (errors) => {
      printLog(errors, 'Form Errors');
    },
    // @ts-expect-error Error because validator is only on email.
    extend: validator({ schema: validations }),
  });

  const checkEldMatch = (
    truckNumber: string,
    trailerNumber: string,
  ): boolean => {
    if (truckNumber === '' && trailerNumber === '') {
      return false;
    }

    const match = eldAssetList.find((asset) => {
      const value = asset.value.trim();
      const truckNum = truckNumber.trim();
      const trailerNum = trailerNumber.trim();

      return (
        (!!truckNum && value === truckNum) ||
        (!!trailerNum && value === trailerNum)
      );
    });

    if (match && match.value !== '') {
      setFields('eldAssetId', match.value);
      setEldAssetMatch(true);
      return true;
    }

    return false;
  };

  onMount(() => {
    const preferedTrackingService = getTrackingProviderLabel(
      orderStore.order.trackingVisibilityProvider,
    );
    if (preferedTrackingService === 'Macropoint') {
      setMacroOverride(true);
      setTrackingPreference(true);
    }
    if (isFeatureFlagEnabled(FeatureFlags.TrackingFrequency)()) {
      setFields('TrackingFrequencyMacro', '15');
    }
    if (isFeatureFlagEnabled(FeatureFlags.TruckerToolsEnabledByOffice)()) {
      setIsTruckerToolsOffice(true);
    }
    if (featureFlagGetCustomerFeatures.featureFlagCustomer.TruckerTools) {
      setIsTruckerToolsCustomer(true);
    }
    //set fields based on the load (phone, trailer, temps, truck number)
    if (load() !== null) {
      setFields(
        'trackingStopWindows',
        load()?.stops?.map((val) => {
          return { Id: val.id, EDT: '', ETA: '' };
        }) || [],
      );
      setFields('loadId', load()?.id);
      setFields('driverPhone', load()?.driverPhoneNumber ?? '');
      setFields('truckNumberMacro', load()?.truckNumber ?? '');
      setFields('trailerNumberMacro', load()?.trailerNumber ?? '');
      setFields('trackingEmailMacro', load()?.trackingEmail ?? '');
      setFields('trailerNumberTruckerTools', load()?.trailerNumber ?? '');
      setFields('truckNumberTruckerTools', load()?.truckNumber ?? '');
      setFields('minTempTruckerTools', load()?.minTemperature ?? '');
      setFields('maxTempTruckerTools', load()?.maxTemperature ?? '');
      setFields('tempUomTruckerTools', load()?.temperatureUnit ?? '');
      setFields('brokerEmailTruckerTools', orderRepEmail() ?? '');
      setFields('trackingEmailTruckerTools', load()?.trackingEmail ?? '');
      const truckNumber = load()?.truckNumber ?? '';
      const trailerNumber = load()?.trailerNumber ?? '';

      checkEldMatch(truckNumber, trailerNumber);
      setHasPhoneNumber(Boolean(load()?.driverPhoneNumber));
      if (orderRepEmail() !== coveredByEmail()) {
        setFields(
          'brokerEmailTruckerTools',
          `${orderRepEmail()}, ${coveredByEmail()}`,
        );
      } else {
        setFields('brokerEmailTruckerTools', orderRepEmail() ?? '');
      }
    }
    //set fields based on the loadTrackingRequest (emails, ETA, EDT)
    if (loadTrackingRequest() !== null) {
      setTtSubmitLabel('UPDATE TRACKING');

      setFields(
        'brokerEmailTruckerTools',
        loadTrackingRequest()?.brokerEmail ?? '',
      );
      setFields(
        'shipperEmailTruckerTools',
        loadTrackingRequest()?.shipperEmail ?? '',
      );
      setFields(
        'carrierDispatchEmailTruckerTools',
        loadTrackingRequest()?.carrierDispatchEmail ?? '',
      );
      setFields(
        'trackingStopWindows',
        loadTrackingRequest()?.trackingStopWindows.map((window) => ({
          Id: window.id,
          ETA: window.eta,
          EDT: window.edt,
        })),
      );
    }
  });

  onCleanup(() => {
    if (isTruckerToolsOffice() || isTruckerToolsCustomer()) {
      updateLoadPropertyAtIndex({
        driverPhoneNumber: data().driverPhone,
      });
      updateLoadPropertyAtIndex({
        truckNumber: data().truckNumberTruckerTools,
      });
      updateLoadPropertyAtIndex({
        trailerNumber: data().trailerNumberTruckerTools,
      });
      if (loadTrackingRequest() !== null) {
        setOrderStore(
          'order',
          'loads',
          index(),
          'loadTrackingRequest',
          (loadTrackingRequest) => {
            return {
              ...loadTrackingRequest,
              trackingStopWindows: data().trackingStopWindows.map((window) => ({
                id: window.Id,
                eta: window.ETA,
                edt: window.EDT,
              })),
              brokerEmail: data().brokerEmailTruckerTools,
              shipperEmail: data().shipperEmailTruckerTools,
              carrierDispatchEmail: data().carrierDispatchEmailTruckerTools,
            };
          },
        );
      }
    }
  });

  const getPrefferedService = () => {
    const trackingProvider = getTrackingProviderLabel(
      orderStore.order.trackingVisibilityProvider,
    );
    if (trackingProvider !== undefined) {
      return (
        <span class={cls.trackingInfoStyle}>
          {`This customer prefers ${trackingProvider} for tracking.`}
        </span>
      );
    }
  };

  return (
    <form ref={form}>
      <div class={cls.modalMetaData}>
        <div class={cls.infoGridStyle}>
          <InformationIcon style={{ 'font-size': '24px', color: '#026EA1' }} />
          <span class={cls.trackingInfoStyle}>
            If you select tracking, any fees will appear as an adjustment on
            this load
          </span>
        </div>
        <Divider />
        <BasicTable<LastUsedTopStopModel>
          columns={columns}
          rows={orderStore.order.loads[orderStore.activeTab.index]?.stops ?? []}
          footerBorder={false}
        />
        {hasTrackingPreference() && (
          <div class={cls.warningInfoGrid}>
            <AlertIcon style={{ 'font-size': '24px', color: '#F59D25' }} />
            {getPrefferedService()}
          </div>
        )}
        <div class="flex gap-4 pl-12 pr-12 justify-center">
          <Show when={!isTruckerToolsOffice() && !isTruckerToolsCustomer()}>
            <div class="flex flex-col gap-4 w-full border-2  rounded-lg p-8">
              <div class="flex w-[167px] h-[92px] self-center">
                <img src={macroPoint} alt="" />
              </div>
              <Divider />
              <TextField
                type="text"
                label="Truck"
                id="truck"
                onChange={(value: string) => {
                  setFields('truckNumberMacro', value);
                }}
                value={data().truckNumberMacro || ''}
                dataTestId="truck-textInput"
              />
              <TextField
                type="text"
                label="Trailer"
                id="trailer"
                onChange={(value: string) => {
                  setFields('trailerNumberMacro', value);
                }}
                value={data().trailerNumberMacro || ''}
                dataTestId="trailer-textInput"
              />
              <div class={cls.infoGridStyle}>
                <InformationIcon
                  style={{ 'font-size': '24px', color: '#026EA1' }}
                />
                <span class={cls.trackingInfoStyle}>
                  Enter multiple emails separated by commas. Max 200 characters.
                </span>
              </div>
              <Input
                id="email"
                label="Email"
                maxChar={200}
                type="text"
                infoText="Send tracking updates to contact(s)"
                placeholder="email1@example.com, email2@example.com"
                value={data().trackingEmailMacro}
                error={errors().trackingEmailMacro}
                dataTestId="email-textInput"
                onChange={(e: { target: { value: string } }) => {
                  setFields('trackingEmailMacro', e.target.value);
                }}
              />
              <Show
                when={isFeatureFlagEnabled(FeatureFlags.TrackingFrequency)()}
              >
                <SelectInput
                  label="Frequency"
                  menuItems={trackingFrequency}
                  value={data().TrackingFrequencyMacro}
                  onChange={(value) => {
                    setFields('TrackingFrequencyMacro', value);
                  }}
                  // TODO: Do we need this?
                  // renderValue={(selected) => {
                  //   return (
                  //     trackingFrequency.find((v) => v.value === selected)
                  //       ?.label ?? ''
                  //   );
                  // }}
                  dataTestId="accept-tracking-modal-frequency"
                />
              </Show>
              <div class={cls.infoGridStyle}>
                <InformationIcon
                  style={{ 'font-size': '24px', color: '#026EA1' }}
                />
                <span class={cls.trackingInfoStyle}>
                  Optional: Enter truck and trailer number to enable ELD
                  Tracking
                </span>
              </div>
              <div class={cls.trackingFrequency}>
                <span class={cls.fontBold}>Every 15-20 Minutes:</span>
                <div>
                  <span class="font-bold text-3xl">$2.50 /</span>
                  <span class={cls.fontBold}>Load</span>
                </div>
                <Show
                  when={
                    getTrackingProviderLabel(
                      orderStore.order.trackingVisibilityProvider,
                    ) === 'Project44'
                  }
                >
                  <Checkbox
                    onChange={(prev) => setMacroOverride(prev)}
                    name="Override"
                    label="Override"
                    dataTestId="override-checkboxInput"
                    size="xs"
                    color="info"
                    class="ml-[-9px]"
                  />
                </Show>
              </div>
              <Button
                label="Select"
                type="submit"
                variant="contained"
                onClick={() => {
                  setFields('serviceId', TrackingProviders.Macropoint);
                }}
                isLoading={isMacroLoading()}
                disabled={
                  (hasTrackingPreference() && !isMacroOverride()) ||
                  isMacroLoading()
                }
                dataTestId={formatDataTestId('Select', 'button')}
              />
            </div>
          </Show>
          {/* RIP Project 44 */}
          <Show when={false}>
            <div class="flex flex-col gap-4 w-full border-2  rounded-lg p-8">
              <div class={cls.logo}>
                <img src={project44} alt="" />
              </div>
              <Divider />
              <div class={cls.trackingFrequency}>
                <span class={cls.fontBold}>Every 30 Minutes:</span>
                <div>
                  <span class="font-bold text-3xl">$3.50 /</span>
                  <span class={cls.fontBold}>Load</span>
                </div>
                <Show
                  when={
                    getTrackingProviderLabel(
                      orderStore.order.trackingVisibilityProvider,
                    ) === 'Macropoint'
                  }
                >
                  <Checkbox
                    onChange={(prev) => setProjectOverride(prev)}
                    name="Override"
                    label="Override"
                    dataTestId="override-checkboxInput"
                    size="xs"
                    color="info"
                    class="ml-[-9px]"
                  />
                </Show>
              </div>
              <div class={cls.project44Btn}>
                <Button
                  class={cls.fullWidth}
                  label="Select"
                  type="submit"
                  dataTestId={formatDataTestId('Select', 'button')}
                  variant="contained"
                  onClick={() => {
                    setFields('serviceId', TrackingProviders.Project44);
                  }}
                  isLoading={isProject44Loading()}
                  disabled={
                    (hasTrackingPreference() && !isProjectOverride()) ||
                    isProject44Loading()
                  }
                />
              </div>
            </div>
          </Show>
          <Show when={isTruckerToolsOffice() || isTruckerToolsCustomer()}>
            <div class="flex flex-col gap-4 w-full border-2  rounded-lg p-8">
              <div class="flex w-80 h-24 self-center">
                <img src={truckerTools} alt="" />
              </div>
              <Divider />
              <div class="text-armstrong-primary text-lg font-medium font-roboto">
                Tracking Details
              </div>
              <Show when={noTracking()}>
                <TrackingBlocked />
              </Show>
              <Show
                when={
                  isTrailerNumberDirty() ||
                  isTruckNumberDirty() ||
                  isPhoneNumberDirty()
                }
              >
                <TruckerToolsCaution />
              </Show>
              <Show
                when={
                  !eldAssetMatch() &&
                  (data().trailerNumberTruckerTools !== '' ||
                    data().truckNumberTruckerTools !== '')
                }
              >
                <EldCaution />
              </Show>
              <CautionInput
                error={noTracking()}
                caution={isTruckNumberDirty()}
                label="Truck Number"
                value={data().truckNumberTruckerTools || ''}
                onInput={() => {
                  setIsTruckNumberDirty(true);
                }}
                onChange={(e: { target: { value: string } }) => {
                  setFields('truckNumberTruckerTools', e.target.value);
                  checkEldMatch(
                    e.target.value,
                    data().trailerNumberTruckerTools,
                  );
                }}
              />
              <CautionInput
                caution={isTrailerNumberDirty()}
                error={noTracking()}
                label="Trailer Number"
                value={data().trailerNumberTruckerTools || ''}
                onInput={() => {
                  setIsTrailerNumberDirty(true);
                }}
                onChange={(e: { target: { value: string } }) => {
                  setFields('trailerNumberTruckerTools', e.target.value);
                  checkEldMatch(data().truckNumberTruckerTools, e.target.value);
                }}
              />
              <CautionPhoneInput
                resetDirty={orderStore.resetDirty}
                onInput={() => {
                  setIsPhoneNumberDirty(true);
                }}
                caution={isPhoneNumberDirty()}
                error={
                  orderStore.orderFormError &&
                  orderStore.orderFormError[
                    `loads[${props.tabIndex}].driverPhoneNumber`
                  ]
                }
                label="Primary Driver Phone"
                value={props.load.driverPhoneNumber ?? ''}
                onChange={(_value) => {
                  setFields('driverPhone', _value);
                  if (_value !== '') {
                    updateLoadPropertyAtIndex({
                      driverPhoneNumber: _value,
                    });
                    setHasPhoneNumber(true);
                  } else {
                    setHasPhoneNumber(false);
                  }
                }}
                onBlur={async () => {
                  await validateFieldForOrder(
                    `loads.${props.tabIndex}.driverPhoneNumber`,
                    `loads[${props.tabIndex}].driverPhoneNumber`,
                  );
                }}
                disabled={orderStore.isReadOnly}
                dataTestId="primary-driver-phone-phoneInput"
              />
              <div class="text-armstrong-primary font-roboto">
                <Show when={isReefer()}>
                  <div class=" text-lg font-medium">Reefer Details</div>
                  <div class="text-gray-500 text-sm font-roboto">
                    Reefer details are only applicable when using ELD tracking
                    with temperature-enabled trailer assets. These details are
                    set from Reefer Settings in Load Details.
                  </div>
                  <div class="flex justify-between mt-2 w-[90%]">
                    <div>
                      <div class="text-sm font-medium">Min Temp.</div>
                      <div class="text-lg text-black">
                        {data().minTempTruckerTools ?? '32'}
                      </div>
                    </div>
                    <div>
                      <div class="text-sm font-medium">Max Temp.</div>
                      <div class="text-lg text-black">
                        {data().maxTempTruckerTools ?? '32'}
                      </div>
                    </div>
                    <div>
                      <div class="text-sm font-medium">Unit of Measurement</div>
                      <div class="text-lg text-black">
                        {data().tempUomTruckerTools}
                      </div>
                    </div>
                  </div>
                </Show>
                <div class=" text-lg font-medium mt-4">
                  Tracking Notifications
                </div>
                <div class="text-gray-500 text-sm font-roboto">
                  All notifications are sent directly from Trucker Tools.
                  Notification types and frequency vary based on the contact
                  type.
                </div>
              </div>
              <Show when={isTruckerToolsCustomer() && !isTruckerToolsOffice()}>
                <TextField
                  type="text"
                  label="Email"
                  id="email"
                  onChange={(value: string) => {
                    setFields('trackingEmailTruckerTools', value);
                  }}
                  error={errors().trackingEmailTruckerTools}
                  value={data().trackingEmailTruckerTools}
                  dataTestId="email-textInput"
                />
                <div class={cls.infoGridStyle}>
                  <InformationIcon
                    style={{ 'font-size': '24px', color: '#026EA1' }}
                  />
                  <span class={cls.trackingInfoStyle}>
                    This email address will receive all tracking updates from
                    Trucker Tools.
                  </span>
                </div>
              </Show>
              <Show when={isTruckerToolsOffice()}>
                <div class={cls.infoGridStyle}>
                  <InformationIcon
                    style={{ 'font-size': '24px', color: '#026EA1' }}
                  />
                  <span class={cls.trackingInfoStyle}>
                    Enter multiple emails separated by commas. Max 200
                    characters per field.
                  </span>
                </div>
                <Input
                  label="Shipper Email"
                  maxChar={200}
                  type="email"
                  infoText="Send tracking updates to customer contact(s)"
                  placeholder="shipper1@example.com, shipper2@example.com"
                  value={data().shipperEmailTruckerTools}
                  onChange={(e: { target: { value: string } }) => {
                    const value = e.target.value;
                    setFields('shipperEmailTruckerTools', value);
                    if (!isValidEmailChain(value)) {
                      setShipperEmailError('Invalid email(s)');
                    } else {
                      setShipperEmailError(null);
                    }
                  }}
                  error={shipperEmailError()}
                />
                <Input
                  label="Broker Email"
                  maxChar={200}
                  type="email"
                  infoText="Send tracking updates to internal user(s)"
                  placeholder="broker1@example.com, broker2@example.com"
                  value={data().brokerEmailTruckerTools}
                  error={brokerEmailError()}
                  onChange={(e: { target: { value: string } }) => {
                    const value = e.target.value;
                    setFields('brokerEmailTruckerTools', value);
                    if (!isValidEmailChain(value)) {
                      setBrokerEmailError('Invalid email(s)');
                    } else {
                      setBrokerEmailError(null);
                    }
                  }}
                />
                <Input
                  label="Carrier/Dispatch Email"
                  maxChar={200}
                  type="email"
                  infoText="Send tracking updates to carrier/dispatch contact(s)"
                  placeholder="dispatch1@example.com, dispatch2@example.com"
                  value={data().carrierDispatchEmailTruckerTools}
                  error={carrierDispatchEmailError()}
                  onChange={(e: { target: { value: string } }) => {
                    const value = e.target.value;
                    setFields('carrierDispatchEmailTruckerTools', value);
                    if (!isValidEmailChain(value)) {
                      setCarrierDispatchEmailError('Invalid email(s)');
                    } else {
                      setCarrierDispatchEmailError(null);
                    }
                  }}
                />
              </Show>
              <div class="mt-auto flex flex-col gap-4 items-center">
                <div class="font-bold text-lg font-roboto">
                  Every 15 Minutes
                </div>
                <Button
                  class="w-full!"
                  isLoading={isTruckerToolsLoading()}
                  disabled={
                    isTruckerToolsLoading() ||
                    noTracking() ||
                    Boolean(
                      orderStore.orderFormError?.[
                        `loads[${props.tabIndex}].driverPhoneNumber`
                      ] ?? false,
                    )
                  }
                  label={ttSubmitLabel()}
                  type="submit"
                  variant="contained"
                  onClick={() => {
                    setFields('serviceId', TrackingProviders.TruckerTools);
                  }}
                  dataTestId={formatDataTestId('Select', 'button')}
                />
              </div>
            </div>
          </Show>
        </div>
      </div>
      <div class={cls.trackingBtn}>
        <Button
          variant="outlined"
          label="No Thanks"
          startIcon={<CloseCircleIcon />}
          onClick={() => {
            props.setTracking(
              orderStore.order.loads[props.tabIndex].trackingEnabled,
            );
            closeModal(props.modalId);
          }}
          dataTestId={formatDataTestId('No Thanks', 'button')}
        />
      </div>
    </form>
  );
};
